import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';

// Import styles
import './App.css';
import './index.css';

// Import providers and router
import { AppProvider } from './contexts/AppContext';
import { ExhibitionProvider } from './contexts/ExhibitionContext';
import AppRouter from './router/AppRouter';
import ErrorDisplay from './components/ErrorDisplay';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <ErrorBoundary
      FallbackComponent={({ error, resetErrorBoundary }) => (
        <ErrorDisplay 
          error={error} 
          onRetry={resetErrorBoundary}
          fullScreen
          title="שגיאה באפליקציה"
        />
      )}
      onError={(error, errorInfo) => {
        // Log error to external service in production
        if (process.env.NODE_ENV === 'production') {
          console.error('Application Error:', error, errorInfo);
          // TODO: Send to error reporting service (Sentry, LogRocket, etc.)
        }
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AppProvider>
          <ExhibitionProvider>
            <AppRouter />
          </ExhibitionProvider>
        </AppProvider>
        
        {/* React Query DevTools - only in development */}
        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;